'use client';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import { useLocale } from 'use-intl';
import Image from '../../../image';
import SectionTitle from './SectionTitle';
import SectionYearSelector from './SectionYearSelector';
import styles from './ExhibitionSection.module.css';
import { fetchConferenceExhibitionByYear } from '../api';
import { getConferenceExhibitionTags } from '../exhibition.helpers';
import type {
  ConferenceExhibitionItem,
  ConferenceExhibitionListResp,
  ConferenceSite,
} from '../types';

interface ExhibitionSectionProps {
  data?: ConferenceExhibitionListResp;
  title?: string;
  years?: number[];
  site?: ConferenceSite;
}

const NAV_FADE_HEIGHT = 114;
const NAV_SCROLL_REVEAL_GAP = 12;
const MOBILE_NAV_SCROLL_EXTRA_REVEAL = 16;

const getItemKey = (item: ConferenceExhibitionItem, index: number) =>
  item.conferenceExhibitionID || `conference-exhibition-${index}`;

const getLocalizedText = (
  item: ConferenceExhibitionItem,
  field: 'name' | 'shortName' | 'introduction' | 'tags',
  locale: 'zh' | 'en'
) => {
  const valueMap = {
    name: {
      zh: item.nameCN || item.nameEN,
      en: item.nameEN || item.nameCN,
    },
    shortName: {
      zh: item.shortNameCN || item.shortNameEN || item.nameCN || item.nameEN,
      en: item.shortNameEN || item.shortNameCN || item.nameEN || item.nameCN,
    },
    introduction: {
      zh: item.introductionCN || item.introductionEN,
      en: item.introductionEN || item.introductionCN,
    },
    tags: {
      zh: item.tagsCN || item.tagsEN,
      en: item.tagsEN || item.tagsCN,
    },
  } as const;

  return valueMap[field][locale] || '';
};

export const calculateDesktopNavFadeState = ({
  clientHeight,
  contentHeight,
  scrollTop,
}: {
  clientHeight: number;
  contentHeight: number;
  scrollTop: number;
}) => {
  // 左侧导航改回原生滚动条后，仅保留“是否还有未露出的内容”这一个展示态判断。
  const maxScrollTop = Math.max(contentHeight - clientHeight, 0);
  if (maxScrollTop <= 1 || contentHeight <= 0 || clientHeight <= 0) {
    return {
      isOverflowing: false,
      showFade: false,
    };
  }

  const normalizedScrollTop = Math.min(Math.max(scrollTop, 0), maxScrollTop);
  return {
    isOverflowing: true,
    showFade: contentHeight - (normalizedScrollTop + clientHeight) > 1,
  };
};

const getDesktopNavContentHeight = (
  navScroll: HTMLDivElement,
  lastTab?: HTMLButtonElement | null
) => {
  // 以最后一个 tab 的真实底部兜底，避免容器高度计算和实际内容末端不一致。
  const lastTabBottom = lastTab
    ? lastTab.offsetTop + lastTab.offsetHeight
    : navScroll.scrollHeight;
  return Math.max(navScroll.scrollHeight, lastTabBottom);
};

const shouldShowDesktopNavFade = (
  navScroll: HTMLDivElement,
  lastTab?: HTMLButtonElement | null
) => {
  const contentHeight = getDesktopNavContentHeight(navScroll, lastTab);
  return contentHeight - (navScroll.scrollTop + navScroll.clientHeight) > 1;
};

/**
 * 大会展区组件
 */
export default function ExhibitionSection({
  data,
  title,
  years,
  site,
}: ExhibitionSectionProps) {
  const locale = useLocale() === 'zh' ? 'zh' : 'en';
  const isOverseaSite = site === 'en';
  const headingClassName = isOverseaSite
    ? `${styles.heading} ${styles.headingOversea}`
    : styles.heading;
  const mobileHeadingClassName = isOverseaSite
    ? `${styles.mobileHeading} ${styles.mobileHeadingOversea}`
    : styles.mobileHeading;
  const mobileDescriptionClassName = isOverseaSite
    ? `${styles.mobileDescription} ${styles.mobileDescriptionOversea}`
    : styles.mobileDescription;
  const [isPending, startTransition] = useTransition();
  const requestIdRef = useRef(0);
  const [sectionData, setSectionData] = useState<ConferenceExhibitionListResp>(
    data || { list: [], total: 0 }
  );
  const [selectedYear, setSelectedYear] = useState(() => years?.[0] || data?.conferenceYear || new Date().getFullYear());
  const list = useMemo(() => sectionData?.list || [], [sectionData]);
  const [activeItemKey, setActiveItemKey] = useState('');
  const navScrollRef = useRef<HTMLDivElement>(null);
  const mobileNavScrollRef = useRef<HTMLDivElement>(null);
  const desktopTabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const mobileTabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const lastAutoScrolledTabKeyRef = useRef('');
  const [desktopNavScrollState, setDesktopNavScrollState] = useState({
    isOverflowing: false,
    showFade: false,
  });
  const [mobileNavScrollState, setMobileNavScrollState] = useState({
    isOverflowing: false,
    showFade: false,
  });

  useEffect(() => {
    setSectionData(data || { list: [], total: 0 });
    if (years?.[0]) {
      setSelectedYear(years[0]);
    }
  }, [data, years]);

  useEffect(() => {
    const firstKey = list[0] ? getItemKey(list[0], 0) : '';
    const currentExists = list.some(
      (item, index) => getItemKey(item, index) === activeItemKey
    );

    if (!currentExists) {
      setActiveItemKey(firstKey);
    }
  }, [activeItemKey, list]);

  const activeItem = useMemo(
    () =>
      list.find(
        (item, index) => getItemKey(item, index) === activeItemKey
      ) || list[0],
    [activeItemKey, list]
  );
  const resolvedActiveItemKey = useMemo(() => {
    const activeIndex = list.findIndex((item) => item === activeItem);
    return activeIndex >= 0 && activeItem
      ? getItemKey(activeItem, activeIndex)
      : '';
  }, [activeItem, list]);
  const activeTags = useMemo(
    () => (activeItem ? getConferenceExhibitionTags(activeItem, locale) : []),
    [activeItem, locale]
  );
  const showActiveTags = locale === 'zh' && activeTags.length > 0;
  const emptyText = locale === 'zh' ? '敬请期待' : 'Coming Soon';
  const lastItemKey = useMemo(
    () =>
      list.length ? getItemKey(list[list.length - 1], list.length - 1) : '',
    [list]
  );
  const yearOptions = useMemo(() => years || [], [years]);

  const handleYearChange = (year: number) => {
    if (year === selectedYear) {
      return;
    }
    setSelectedYear(year);
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    startTransition(() => {
      void fetchConferenceExhibitionByYear({
        conferenceYear: year,
        site,
      }).then((nextData) => {
        if (requestIdRef.current === requestId) {
          setSectionData(nextData);
        }
      });
    });
  };

  const syncDesktopNavScrollState = useCallback(() => {
    const navScroll = navScrollRef.current;
    if (!navScroll) {
      return {
        isOverflowing: false,
        showFade: false,
      };
    }

    const lastTab = lastItemKey ? desktopTabRefs.current[lastItemKey] : null;
    const nextState = calculateDesktopNavFadeState({
      clientHeight: navScroll.clientHeight,
      contentHeight: getDesktopNavContentHeight(navScroll, lastTab),
      scrollTop: navScroll.scrollTop,
    });

    setDesktopNavScrollState((prev) =>
      prev.isOverflowing === nextState.isOverflowing &&
      prev.showFade === nextState.showFade
        ? prev
        : nextState
    );
    return nextState;
  }, [lastItemKey]);

  const syncMobileNavScrollState = useCallback(() => {
    const mobileNavScroll = mobileNavScrollRef.current;
    if (!mobileNavScroll) {
      return {
        isOverflowing: false,
        showFade: false,
      };
    }

    const nextState = {
      isOverflowing: mobileNavScroll.scrollWidth - mobileNavScroll.clientWidth > 1,
      showFade:
        mobileNavScroll.scrollWidth -
          (mobileNavScroll.scrollLeft + mobileNavScroll.clientWidth) >
        1,
    };

    setMobileNavScrollState((prev) =>
      prev.isOverflowing === nextState.isOverflowing &&
      prev.showFade === nextState.showFade
        ? prev
        : nextState
    );
    return nextState;
  }, []);

  useEffect(() => {
    syncDesktopNavScrollState();
    window.addEventListener('resize', syncDesktopNavScrollState);
    return () => {
      window.removeEventListener('resize', syncDesktopNavScrollState);
    };
  }, [syncDesktopNavScrollState]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      syncMobileNavScrollState();
    });
    window.addEventListener('resize', syncMobileNavScrollState);
    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', syncMobileNavScrollState);
    };
  }, [syncMobileNavScrollState, resolvedActiveItemKey, list.length]);

  useEffect(() => {
    const navScroll = navScrollRef.current;
    const activeTab = desktopTabRefs.current[resolvedActiveItemKey];
    if (!navScroll || !activeTab || !resolvedActiveItemKey) {
      return;
    }
    if (lastAutoScrolledTabKeyRef.current === resolvedActiveItemKey) {
      return;
    }

    // 选中被底部遮盖的 tab 时，主动把它滚入完整可视区，保持和 Figma 一致的浏览体验。
    const tabTop = activeTab.offsetTop;
    const tabBottom = tabTop + activeTab.offsetHeight;
    const visibleTop = navScroll.scrollTop;
    const fadeReservedHeight = shouldShowDesktopNavFade(
      navScroll,
      activeTab
    )
      ? NAV_FADE_HEIGHT
      : 0;
    const visibleBottom =
      navScroll.scrollTop + navScroll.clientHeight - fadeReservedHeight;

    if (tabBottom > visibleBottom - NAV_SCROLL_REVEAL_GAP) {
      lastAutoScrolledTabKeyRef.current = resolvedActiveItemKey;
      navScroll.scrollTo({
        top:
          tabBottom -
          navScroll.clientHeight +
          fadeReservedHeight +
          NAV_SCROLL_REVEAL_GAP,
        behavior: 'smooth',
      });
      return;
    }

    if (tabTop < visibleTop + NAV_SCROLL_REVEAL_GAP) {
      lastAutoScrolledTabKeyRef.current = resolvedActiveItemKey;
      navScroll.scrollTo({
        top: Math.max(tabTop - NAV_SCROLL_REVEAL_GAP, 0),
        behavior: 'smooth',
      });
      return;
    }

    lastAutoScrolledTabKeyRef.current = resolvedActiveItemKey;
  }, [resolvedActiveItemKey]);

  useEffect(() => {
    const mobileNavScroll = mobileNavScrollRef.current;
    const activeMobileTab = mobileTabRefs.current[resolvedActiveItemKey];
    if (!mobileNavScroll || !activeMobileTab) {
      return;
    }

    const tabLeft = activeMobileTab.offsetLeft;
    const tabRight = tabLeft + activeMobileTab.offsetWidth;
    const visibleLeft = mobileNavScroll.scrollLeft;
    const visibleRight = visibleLeft + mobileNavScroll.clientWidth;
    const mobileRevealGap = NAV_SCROLL_REVEAL_GAP + MOBILE_NAV_SCROLL_EXTRA_REVEAL;

    if (tabRight > visibleRight - mobileRevealGap) {
      mobileNavScroll.scrollTo({
        left: tabRight - mobileNavScroll.clientWidth + mobileRevealGap,
        behavior: 'smooth',
      });
      syncMobileNavScrollState();
      return;
    }

    if (tabLeft < visibleLeft + mobileRevealGap) {
      mobileNavScroll.scrollTo({
        left: Math.max(tabLeft - mobileRevealGap, 0),
        behavior: 'smooth',
      });
      syncMobileNavScrollState();
    }
  }, [resolvedActiveItemKey, syncMobileNavScrollState]);

  return (
    <section id="exhibition" className={styles.section}>
      <div className={styles.container}>
        <div className={styles.header}>
          <SectionTitle
            className={locale === 'en' ? styles.englishTitle : ''}
          >
            {title}
          </SectionTitle>
          <SectionYearSelector
            selectedYear={selectedYear}
            years={yearOptions}
            onChange={handleYearChange}
          />
        </div>
        {activeItem ? (
          <>
            <div className={styles.content}>
              <div className={styles.desktopNavPanel}>
                <div
                  ref={navScrollRef}
                  className={styles.navScrollArea}
                  onScroll={syncDesktopNavScrollState}
                >
                  <div className={styles.navList}>
                    {list.map((item, index) => {
                      const itemKey = getItemKey(item, index);
                      const isActive = itemKey === resolvedActiveItemKey;
                      return (
                        <button
                          key={`${itemKey}-desktop`}
                          ref={(node) => {
                            desktopTabRefs.current[itemKey] = node;
                          }}
                          className={`${styles.navButton} ${
                            isActive ? styles.activeNavButton : ''
                          } ${isOverseaSite ? styles.navButtonOversea : ''} ${
                            isOverseaSite && isActive
                              ? styles.activeNavButtonOversea
                              : ''
                          }`}
                          data-testid="exhibition-desktop-tab"
                          onClick={() => setActiveItemKey(itemKey)}
                          type="button"
                          title={getLocalizedText(item, 'shortName', locale) || '-'}
                        >
                          <span>
                            {getLocalizedText(item, 'shortName', locale) || '-'}
                          </span>
                          {isActive ? (
                            <svg
                              className={styles.activeNavArrow}
                              width="6"
                              height="10"
                              viewBox="0 0 6 10"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-hidden="true"
                            >
                              <path
                                d="M0 1.00198C0 0.111077 1.07714 -0.335089 1.70711 0.294875L5.62102 4.20879C6.01154 4.59931 6.01154 5.23248 5.62102 5.623L1.70711 9.53691C1.07714 10.1669 0 9.72071 0 8.82981V4.91589V1.00198Z"
                                fill="currentColor"
                              />
                            </svg>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {desktopNavScrollState.showFade ? (
                  <div className={styles.navFade} aria-hidden="true" />
                ) : null}
              </div>

              <div className={styles.detailPanel}>
                <div className={styles.heroWrap}>
                  {activeItem.imageURL ? (
                    <Image
                      src={activeItem.imageURL}
                      alt={getLocalizedText(activeItem, 'name', locale) || '-'}
                      className={styles.heroImage}
                      loading="lazy"
                    />
                  ) : (
                    <div className={styles.heroPlaceholder}>{emptyText}</div>
                  )}
                </div>

                <div className={styles.detailBody}>
                  <div className={styles.titleRow}>
                    <h3
                      className={headingClassName}
                      title={getLocalizedText(activeItem, 'name', locale) || '-'}
                    >
                      {getLocalizedText(activeItem, 'name', locale) || '-'}
                    </h3>
                    {showActiveTags ? (
                      <div className={styles.tags}>
                        {activeTags.map((tag, index) => (
                          <span key={index} className={styles.tag} title={tag}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <p
                    className={styles.description}
                    title={getLocalizedText(activeItem, 'introduction', locale) || emptyText}
                  >
                    {getLocalizedText(activeItem, 'introduction', locale) || emptyText}
                  </p>
                </div>
              </div>
            </div>

            <div className={styles.mobileContent}>
              <div className={styles.mobileNavWrap}>
                <div
                  ref={mobileNavScrollRef}
                  className={styles.mobileNavList}
                  onScroll={syncMobileNavScrollState}
                >
                  {list.map((item, index) => {
                    const itemKey = getItemKey(item, index);
                    const isActive = itemKey === resolvedActiveItemKey;
                    return (
                      <button
                        key={`${itemKey}-mobile`}
                        ref={(node) => {
                          mobileTabRefs.current[itemKey] = node;
                        }}
                        className={`${styles.mobileNavButton} ${
                          isActive ? styles.mobileNavButtonActive : ''
                        } ${isOverseaSite ? styles.mobileNavButtonOversea : ''}`}
                        onClick={() => setActiveItemKey(itemKey)}
                        type="button"
                        title={getLocalizedText(item, 'shortName', locale) || '-'}
                      >
                        <span>{getLocalizedText(item, 'shortName', locale) || '-'}</span>
                      </button>
                    );
                  })}
                </div>
                {mobileNavScrollState.isOverflowing && mobileNavScrollState.showFade ? (
                  <div className={styles.mobileNavFade} aria-hidden="true" />
                ) : null}
              </div>

              <div className={styles.mobileDetailPanel}>
                <div className={styles.mobileHeroWrap}>
                  {activeItem.imageURL ? (
                    <Image
                      src={activeItem.imageURL}
                      alt={getLocalizedText(activeItem, 'name', locale) || '-'}
                      className={styles.mobileHeroImage}
                      loading="lazy"
                    />
                  ) : (
                    <div className={styles.mobileHeroPlaceholder}>{emptyText}</div>
                  )}
                </div>

                <div className={styles.mobileDetailBody}>
                  <div className={styles.mobileTitleRow}>
                    <h3
                      className={mobileHeadingClassName}
                      title={getLocalizedText(activeItem, 'name', locale) || '-'}
                    >
                      {getLocalizedText(activeItem, 'name', locale) || '-'}
                    </h3>

                    {showActiveTags ? (
                      <div className={styles.mobileTags}>
                        {activeTags.map((tag, index) => (
                          <span key={index} className={styles.mobileTag} title={tag}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  <p
                    className={mobileDescriptionClassName}
                    title={getLocalizedText(activeItem, 'introduction', locale) || emptyText}
                  >
                    {getLocalizedText(activeItem, 'introduction', locale) || emptyText}
                  </p>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
}
